/**
 * STATIC ADAPTER — the implementation in use today.
 *
 * Serves the fixtures under ./fixtures. Two properties are deliberate:
 *
 *  1. Every method is async. Nothing resolves synchronously, so a component
 *     that forgets to handle a pending state fails here rather than the day the
 *     API is switched on.
 *  2. Reads are pure. Writes (`saveAnalysis`, `saveReport`, `uploadExternalReport`)
 *     echo their argument back and do NOT mutate the fixtures — the session
 *     store owns mutable state, so a page reload restores the seed exactly.
 *     That is what makes the prototype demonstrable: nothing persists.
 */

import type {
  AccountSnapshot,
  ActivationDataset,
  ActivationDeepDive,
  ActivationDemo,
  ActivationReport,
  ActivationSection,
  ActivationVocabulary,
  Analysis,
  BetaRegistration,
  CatalogueModule,
  DatasetDefectProfile,
  DatasetShape,
  DqsAgentSummary,
  DqsDataset,
  DqsScope,
  ExternalReport,
  HumanLayerProfile,
  HumanLayerProfileDraft,
  HumanLayerVisionProfile,
  HumanLayerVisionProfileDraft,
  HumanLayerVoiceProfile,
  HumanLayerVoiceProfileDraft,
  Id,
  MeasurementChannel,
  MmmChannelConfig,
  MmmCostModel,
  MmmOutput,
  MmmPartnership,
  MmmSourceOption,
  Pin,
  PinDraft,
  PriceBookItem,
  PricingReference,
  QaFamily,
  QaRule,
  ResearchVocabulary,
  ServiceQuote,
  ServiceQuoteApproval,
  ServiceScope,
  SessionUser,
  SrConfiguration,
  SrContract,
  SrPartner,
  ZoneCopy,
} from '../contracts';
import type { DataAdapter } from '../ports';
import { ACTIVATION_DEMOS } from './fixtures/activationDemos';
import {
  AR_DATASETS,
  AR_DEEPDIVES,
  AR_SECTIONS,
  AR_VOCABULARY,
  SEED_ACTIVATION,
} from './fixtures/activation';
import { PROFILE_VOCABULARY, SESSION_ACCESS, SESSION_PROFILE } from './fixtures/access';
import { SERVICE_RATES } from './fixtures/servicePricing';
import { buildFallbackQuote } from '@/domain/quote/model';
import { uid } from '@/lib/random';
import { CATALOGUE, SESSION_USER, ZONES } from './fixtures/catalogue';
import {
  DATASET_SHAPES,
  DEFAULT_PROFILE,
  DEFAULT_SHAPE,
  DEFECT_PROFILES,
  DQS_AGENTS,
  DQS_DATASETS,
  DQS_SCOPES,
  QA_FAMILIES,
  QA_RULES,
} from './fixtures/dataQuality';
import { SEED_ANALYSES } from './fixtures/library';
import { CHANNELS, PRICE_BOOK } from './fixtures/measurement';
import {
  MMM_CHANNELS,
  MMM_COST,
  MMM_METRICS,
  MMM_OUTPUTS,
  MMM_PARTNERSHIPS,
  MMM_SOURCES,
} from './fixtures/mmm';
import { mmmVariableCreation } from './mmmVariableCreation';
import { vendorRegister } from './vendorRegister';
import { PRICING_REFERENCE } from './fixtures/pricing';
import { RESEARCH_VOCABULARY, SEED_EXTERNAL } from './fixtures/research';
import { SR_CONFIGURATION, SR_CONTRACTS, SR_PARTNERS } from './fixtures/strategicRelevance';
import { DEMO_USER_ID, HUMAN_LAYER_PROFILE_SEED } from './fixtures/humanLayerProfile';
import { HUMAN_LAYER_VOICE_SEED } from './fixtures/humanLayerVoice';
import { HUMAN_LAYER_VISION_SEED } from './fixtures/humanLayerVision';
import { documentsAdapter } from '../documents/documentsAdapter';

/** Resolves on a microtask, mirroring the shape of a real request. */
const ok = <T>(value: T): Promise<T> => Promise.resolve(value);

/** Rejects the way the HTTP adapter would for an unknown id. */
const notFound = (what: string, id: Id): Promise<never> =>
  Promise.reject(new Error(`${what} "${id}" does not exist.`));

export const staticAdapter: DataAdapter = {
  /**
   * No seed, deliberately. A registration is a named person's stated interest,
   * and inventing one would put fabricated personal data in a fixture. Empty is
   * the honest demo state; `register` echoes, which is the seam the API fills.
   */
  beta: {
    listRegistrations: (): Promise<readonly BetaRegistration[]> => ok([]),
    register: (registration: BetaRegistration): Promise<BetaRegistration> => ok(registration),
  },

  catalogue: {
    listModules: (): Promise<readonly CatalogueModule[]> => ok(CATALOGUE),
    listZones: (): Promise<readonly ZoneCopy[]> => ok(ZONES),
    getSessionUser: (): Promise<SessionUser> => ok(SESSION_USER),
    getAccount: (): Promise<AccountSnapshot> =>
      ok({
        profile: SESSION_PROFILE,
        access: SESSION_ACCESS,
        vocabulary: {
          nationalities: PROFILE_VOCABULARY.nationalities,
          languages: PROFILE_VOCABULARY.languages,
        },
      }),
  },

  library: {
    listAnalyses: (): Promise<readonly Analysis[]> => ok(SEED_ANALYSES),
    // Persistence lives in the session store. This is the seam the API will
    // fill; it echoes so the calling code is already shaped correctly.
    saveAnalysis: (analysis: Analysis): Promise<Analysis> => ok(analysis),
    deleteAnalysis: (): Promise<void> => ok(undefined),
  },

  research: {
    listExternalReports: (): Promise<readonly ExternalReport[]> => ok(SEED_EXTERNAL),
    getVocabulary: (): Promise<ResearchVocabulary> => ok(RESEARCH_VOCABULARY),
    uploadExternalReport: (report: ExternalReport): Promise<ExternalReport> => ok(report),
  },

  // Documents are real local storage, not a fixture. Point 2 of this file's
  // header does not apply to them: an uploaded file is meant to persist, which
  // is exactly why it lives in IndexedDB and not in the session store.
  documents: documentsAdapter,

  measurement: {
    listChannels: (): Promise<readonly MeasurementChannel[]> => ok(CHANNELS),
    listPriceBook: (): Promise<readonly PriceBookItem[]> => ok(PRICE_BOOK),
  },

  // The Marketplace vendor register. Every read throws until the API answers
  // — see ./vendorRegister.ts for why a fixture would be the wrong answer.
  vendorRegister,

  dataQuality: {
    listFamilies: (): Promise<readonly QaFamily[]> => ok(QA_FAMILIES),
    listRules: (): Promise<readonly QaRule[]> => ok(QA_RULES),
    listDatasets: (): Promise<readonly DqsDataset[]> => ok(DQS_DATASETS),
    listScopes: (): Promise<readonly DqsScope[]> => ok(DQS_SCOPES),
    listAgentSummaries: (): Promise<readonly DqsAgentSummary[]> => ok(DQS_AGENTS),
    getDatasetShape: (datasetId: Id): Promise<DatasetShape> =>
      ok(DATASET_SHAPES[datasetId] ?? DEFAULT_SHAPE),
    getDefectProfile: (datasetId: Id): Promise<DatasetDefectProfile> =>
      ok(DEFECT_PROFILES[datasetId] ?? DEFAULT_PROFILE),
  },

  mmm: {
    // The variable-creation reads, every one throwing NotImplementedError until a
    // fixture lands. Spread first so a real implementation added below always wins.
    ...mmmVariableCreation,

    listPartnerships: (): Promise<readonly MmmPartnership[]> => ok(MMM_PARTNERSHIPS),
    listChannelConfig: (): Promise<readonly MmmChannelConfig[]> => ok(MMM_CHANNELS),
    getCostModel: (): Promise<MmmCostModel> => ok(MMM_COST),
    listSources: (): Promise<readonly MmmSourceOption[]> => ok(MMM_SOURCES),
    listMetrics: (): Promise<readonly MmmSourceOption[]> => ok(MMM_METRICS),
    listOutputs: (): Promise<readonly MmmOutput[]> => ok(MMM_OUTPUTS),
    getOutput: (id: Id): Promise<MmmOutput> => {
      const found = MMM_OUTPUTS.find((o) => o.id === id);
      return found ? ok(found) : notFound('MMM output', id);
    },
  },

  strategicRelevance: {
    getConfiguration: (): Promise<SrConfiguration> => ok(SR_CONFIGURATION),
    listPartners: (): Promise<readonly SrPartner[]> => ok(SR_PARTNERS),
    listContracts: (): Promise<readonly SrContract[]> => ok(SR_CONTRACTS),
  },

  pricing: {
    getReference: (): Promise<PricingReference> => ok(PRICING_REFERENCE),
  },

  /**
   * Stands in for the Partnership Net Pricing Engine until it is live (DEC-48).
   *
   * Every quote it issues is stamped `arena-fallback` and carries a provenance
   * line saying so, so nothing on a screen can be mistaken for a quote from the
   * system of record. Acceptance is echoed back rather than stored: there is no
   * engine to record it against yet, and a local store of accepted quotes would
   * be a second commercial record nobody agreed to keep.
   */
  servicePricing: {
    quote: (scope: ServiceScope): Promise<ServiceQuote> =>
      ok(buildFallbackQuote(scope, SERVICE_RATES, new Date().toISOString())),
    acceptQuote: (quoteId: string, approvedBy: string): Promise<ServiceQuoteApproval> =>
      ok({ quoteId, approvedBy, approvedAt: new Date().toISOString() }),
  },

  activation: {
    listReports: (): Promise<readonly ActivationReport[]> => ok(SEED_ACTIVATION),
    saveReport: (report: ActivationReport): Promise<ActivationReport> => ok(report),
    listSections: (): Promise<readonly ActivationSection[]> => ok(AR_SECTIONS),
    listDeepDives: (): Promise<readonly ActivationDeepDive[]> => ok(AR_DEEPDIVES),
    listDatasets: (): Promise<readonly ActivationDataset[]> => ok(AR_DATASETS),
    getVocabulary: (): Promise<ActivationVocabulary> => ok(AR_VOCABULARY),
    getDemo: (id: Id): Promise<ActivationDemo> => {
      const found = ACTIVATION_DEMOS.find((d) => d.id === id);
      return found ? ok(found) : notFound('Activation demo', id);
    },
  },

  /**
   * No seed, deliberately — the same reasoning as `beta`: a pin is one
   * person's own choice, and there is no default set of shortcuts to invent
   * without putting words in someone's mouth. `pin` assigns the id and
   * timestamp, mirroring `acceptQuote`'s precedent for adapter-issued fields.
   * `unpin` and `reorder` are no-ops that echo empty, per this file's header —
   * a demo that wants pinning to feel real holds that state in the session
   * store, the same seam `library` and `activation` already use.
   */
  pins: {
    listPins: (): Promise<readonly Pin[]> => ok([]),
    pin: (draft: PinDraft): Promise<Pin> =>
      ok({ ...draft, id: uid('pin'), order: 0, pinnedAt: new Date().toISOString() }),
    unpin: (): Promise<void> => ok(undefined),
    reorder: (): Promise<readonly Pin[]> => ok([]),
  },

  /**
   * `updateProfile` never writes back to the seed, per this file's header: it
   * builds a fresh record over `HUMAN_LAYER_PROFILE_SEED` and echoes it back
   * with `status: 'active'` and a fresh `updatedAt`, the same "assign at call
   * time" pattern `pin()` uses above. A page reload restores `not_configured`.
   */
  humanLayerProfile: {
    getProfile: (): Promise<HumanLayerProfile> => ok(HUMAN_LAYER_PROFILE_SEED),
    updateProfile: (draft: HumanLayerProfileDraft): Promise<HumanLayerProfile> =>
      ok({
        ...HUMAN_LAYER_PROFILE_SEED,
        identity: draft.identity ?? HUMAN_LAYER_PROFILE_SEED.identity,
        workingPreferences: draft.workingPreferences ?? HUMAN_LAYER_PROFILE_SEED.workingPreferences,
        status: 'active',
        audit: {
          ...HUMAN_LAYER_PROFILE_SEED.audit,
          updatedBy: DEMO_USER_ID,
          updatedAt: new Date().toISOString(),
        },
      }),
  },

  /**
   * `updateVoiceProfile` never writes back to the seed, same pattern as
   * `humanLayerProfile.updateProfile` above: builds a fresh record over
   * `HUMAN_LAYER_VOICE_SEED` and echoes it back with `status: 'active'` and a
   * fresh `updatedAt`. A page reload restores `not_configured`.
   *
   * `consentTimestamp` is stamped here, not taken from the draft — the same
   * "adapter assigns it" precedent `pin()` and `acceptQuote()` set above, so a
   * client cannot assert a false consent time.
   */
  humanLayerVoice: {
    getVoiceProfile: (): Promise<HumanLayerVoiceProfile> => ok(HUMAN_LAYER_VOICE_SEED),
    updateVoiceProfile: (draft: HumanLayerVoiceProfileDraft): Promise<HumanLayerVoiceProfile> =>
      ok({
        ...HUMAN_LAYER_VOICE_SEED,
        attributes: draft.attributes,
        sourceMethod: draft.sourceMethod,
        samples: draft.samples,
        consentRecorded: draft.consentRecorded,
        consentTimestamp: draft.consentRecorded ? new Date().toISOString() : undefined,
        status: 'active',
        audit: {
          ...HUMAN_LAYER_VOICE_SEED.audit,
          updatedBy: DEMO_USER_ID,
          updatedAt: new Date().toISOString(),
        },
      }),
  },

  /**
   * `updateVisionProfile` never writes back to the seed, same pattern as
   * `humanLayerVoice.updateVoiceProfile` immediately above: builds a fresh
   * record over `HUMAN_LAYER_VISION_SEED` and echoes it back with
   * `status: 'active'` and a fresh `updatedAt`. A page reload restores
   * `not_configured`.
   */
  humanLayerVision: {
    getVisionProfile: (): Promise<HumanLayerVisionProfile> => ok(HUMAN_LAYER_VISION_SEED),
    updateVisionProfile: (draft: HumanLayerVisionProfileDraft): Promise<HumanLayerVisionProfile> =>
      ok({
        ...HUMAN_LAYER_VISION_SEED,
        statements: draft.statements,
        status: 'active',
        audit: {
          ...HUMAN_LAYER_VISION_SEED.audit,
          updatedBy: DEMO_USER_ID,
          updatedAt: new Date().toISOString(),
        },
      }),
  },
};
